<template>
	<label :class="{disabled: disabled}" class="wrapper">
		<input
			:id="id"
			:name="name"
			:value="value"
			:checked="checked"
			:disabled="disabled"
			type="checkbox"
			class="input"
			:tabindex="disableTabbing ? -1 : 0"
			v-on="inputListeners"
			@keydown.enter="onEnter"
		>
		<div :class="{disabled: disabled, indeterminate: indeterminate}" class="checkbox" >
			<t-timpex-icon
				v-if="indeterminate"
				class="icon-indeterminate"
				type="bp"
				size="xxs"
				icon="symbol-square"
			/>
			<t-timpex-icon
				v-else
				class="icon"
				type="bp"
				icon="tick"
				size="xs"
			/>
		</div>
		<span>
			<div v-if="label" :class="{disabled: disabled}" class="label">
				{{label}}
			</div>
			<div v-if="description" :class="{disabled: disabled}" class="description">
				{{description}}
			</div>
		</span>
	</label>
</template>

<script>
import TTimpexIcon from '../timpex/icons/TTimpexIcon';
import checkboxMixin from './checkboxMixin';
export default {
	name: 'TCheckbox',
	components: { TTimpexIcon },
	mixins: [checkboxMixin],
	props: {
		/**
		 * State in addition to "checked" and "unchecked". When true, the radio displays a "minus" icon.
		 */
		indeterminate: {
			type: Boolean,
			default: false,
		},
		disableTabbing: {
			type: Boolean,
			default: false
		},
	},
	methods: {
		onEnter(event) {
			event.preventDefault();
			this.$emit('change', !event.target.checked);
			window.focusNext(event);
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/components/evergreen/scss/colors';
@import '~@/components/evergreen/scss/text';
@import '~@/components/evergreen/scss/primary';

.wrapper {
	display: flex;
	cursor: pointer;
	box-sizing: border-box;
	position: relative;
	margin-top: 10px;
	margin-bottom: 10px;
	&:hover input[type='checkbox']:enabled + .checkbox {
		background-image: linear-gradient(to bottom, #FAFBFB, #EAECEE);
		&.indeterminate {
			background-image:
			linear-gradient(
				to bottom,
				darken(get-color($default, 'startColor'),5%),
				darken(get-color($default, 'endColor'),5%)
			);
		}
	}
	&:hover input[type='checkbox']:enabled:checked + .checkbox {
		background-image:
			linear-gradient(
				to bottom,
				darken(get-color($default, 'startColor'),5%),
				darken(get-color($default, 'endColor'),5%)
			);
	}
	&:focus-within .checkbox {
		box-shadow: 0 0 0 2px get-color($blue-scale, 'B4A'),
			inset 0 0 0 1px get-color($neutral-scale, 'N5A'),
			inset 0 -1px 1px 0 get-color($neutral-scale, 'N3A');
	}
	&:active input[type='checkbox']:enabled + .checkbox {
		background-image: none;
		background-color: get-color($blue-scale, 'B3A');
		box-shadow: inset 0 0 0 1px get-color($blue-scale, 'B5A');
		&.indeterminate {
			background-image:
				linear-gradient(
					to bottom,
					darken(get-color($default, 'endColor'),5%),
					darken(get-color($default, 'endColor'),5%)
			);
		}
	}
	&:active input[type='checkbox']:enabled:checked + .checkbox {
		background-image:
			linear-gradient(
				to bottom,
				darken(get-color($default, 'endColor'),5%),
				darken(get-color($default, 'endColor'),5%)
		);
	}
}

.input {
	border: 0px;
	clip: rect(1px, 1px, 1px, 1px);
	height: 1px;
	overflow: hidden;
	padding: 0px;
	position: absolute;
	white-space: nowrap;
	width: 1px;
	opacity: 0;
	&:not(:checked) + .checkbox .icon {
		display: none;
	}
	&:checked + .checkbox {
		color: white;
		background-image: linear-gradient(to bottom, #0788DE, #116AB8);
		box-shadow: inset 0 0 0 1px rgba(67, 90, 111, 0.3), inset 0 -1px 1px 0 get-color($neutral-scale, 'N2A');
	}
	&:not(:enabled):checked + .checkbox {
		color: get-color($icon, 'disabled');
		background-image: linear-gradient(to top, get-color($neutral-scale, 'N2A'), rgba(67, 90, 111, 0.04));
		box-shadow: inset 0 0 0 1px get-color($neutral-scale, 'N4A'),
					inset 0 -1px 1px 0 get-color($neutral-scale, 'N2A');
	}
	&:focus + .checkbox {
		box-shadow: 0 0 0 2px get-color($blue-scale, 'B4A'),
			inset 0 0 0 1px get-color($neutral-scale, 'N5A'),
			inset 0 -1px 1px 0 get-color($neutral-scale, 'N3A');
	}
}

.checkbox {
	height: 16px;
	width: 16px;
	box-sizing: border-box;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 3px;

	-webkit-font-smoothing: antialiased;
	text-decoration: none;
	-webkit-appearance: none;
	-moz-appearance: none;
	border: none;
	outline: none;
	cursor: pointer;
	color: white;
	background-color: white;
	background-image: linear-gradient(to top,
		get-color($neutral-scale, 'N2A'),
		white);
	box-shadow: inset 0 0 0 1px get-color($neutral-scale, 'N4A'),
				inset 0 -1px 1px 0 get-color($neutral-scale, 'N3A');
	&.indeterminate {
		background-image: linear-gradient(to bottom, #0788DE, #116AB8);
	}
}

.checked {
	color: white;
	background-image: linear-gradient(to bottom, #0788DE, #116AB8);
	box-shadow: inset 0 0 0 1px rgba(67, 90, 111, 0.3), inset 0 -1px 1px 0 get-color($neutral-scale, 'N2A');
}

.icon, .icon-indeterminate {
	color: inherit;
}

.icon {
	width: 14px;
}

.icon-indeterminate {
	width: 18px;
}

.label {
	@extend %text-small;
	@extend %font-families-ui;
	font-weight: 600;
	user-select: none;
	margin-left: 8px;
	line-height: 1.33;
	color: get-color($text, 'dark');
	box-sizing: border-box;
	&.disabled {
		color: get-color($text, 'muted');
	}
}
.description {
	@extend %text-small;
	@extend %font-families-ui;
	user-select: none;
	color: get-color($text, 'muted');
	box-sizing: border-box;
	margin-top: 0px;
	margin-bottom: 0px;
	margin-left: 8px;
	line-height: 1.33;
}
.disabled {
	cursor: not-allowed;
}

</style>


<docs>
## Examples

### Checkbox states

```jsx
new Vue({
	data() {
		return {
			isChecked: false
		}
	},
	template: `
	<div>
		<t-button @click="isChecked = !isChecked">Toggle isChecked</t-button>
		<t-checkbox v-model="isChecked" label="Checkbox default"></t-checkbox>
		<t-checkbox label="Checkbox checked" checked></t-checkbox>
		<t-checkbox label="Checkbox disabled" disabled></t-checkbox>
		<t-checkbox label="Checkbox checked disabled" checked disabled></t-checkbox>
		<t-checkbox label="Checkbox indeterminate" indeterminate></t-checkbox>
		<t-checkbox label="Checkbox checked indeterminate" checked indeterminate></t-checkbox>
	</div>
	`
})

```
</docs>
